/**
 * 高知県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は他県と同じテンプレート。
 * 6品が農水省「うちの郷土料理」、5品が高知県公式「おいしい風土こうち」、1品が須崎市公式から。
 *
 * 形のばらつき。
 *  平たいシート（青のり）／散らばる小魚（どろめ）／中空の棒（いたどり）／
 *  小さな卵と立ち上がる黄身（土佐ジローの卵）／自分の体より長い腕（川エビ）／
 *  小さな俵が横一列（田舎ずし）／角切りの塊（ぐる煮）／厚い皮の大玉（土佐文旦）／
 *  細長い魚と厚い切り身（うつぼ）／一尾まるごと（さばの姿ずし）／厚い切り身の列（かつおのたたき）。
 *  器を描く例外は鍋焼きラーメン（土鍋）だけ。須崎市公式が「一番のポイントは器が土鍋であること」と
 *  断定形で書いているので、器を描かないと料理として成立しない。
 *
 * 小夏は入れていない。
 *  宮崎で日向夏をすでに使っており、小夏は同系統の柑橘なので絵が重なる。
 *  かわりに土佐ジローの卵を入れた。既出の鶏7品はすべて肉料理なので、卵なら形態が完全に別。
 *
 * 芋けんぴ・碁石茶・ぼうしパンは入れていない。
 *  国・県・市町村いずれの公式サイトでも解説ページが見つからず、出典が取れなかった。
 *  出典のない料理は出さない。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 「日本三大鍾乳洞」（龍河洞）は国・県の断定形が確認できない。そもそも龍河洞は採らない。
 *  - 竜串の「シコロサンゴの群落は日本一の規模」は市公式が「とされています」という伝聞形。
 *  - 「仁淀ブルー」は県ではなく、流域6市町村でつくる協議会が使う呼び名。県が言っているとは書かない。
 *  - 高知城は「天守と本丸御殿がともに現存する唯一の城」とは書かない。公式の断定形は
 *    「本丸御殿が天守に接続する形式を残すのは高知城だけ」「本丸の建物群がすべて現存するのは高知城だけ」。
 *  - 桂浜の「五色の小石」は高知市公式に記載がない。書かない。
 *  - かつおのたたきの藁焼きは「昔ながらの方法」と添える。家庭ではガス火も使われる。
 *  - 特定の菓子メーカーの商品名は出さない。
 *
 * 本番画像は public/assets/foods/kochi/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：いたどりの油いため・きゅうりと川エビの煮物・田舎ずし・ぐる煮・土佐文旦（店で食べられる品に差し替え）
 * - アイスクリン：明治の横浜の「あいすくりん」や、物資不足で脱脂粉乳や卵を使うものに変わった話は出典にあるが、高知の話ではないので書かない
 * - 清水さばの刺身：ブランド化の年は公式ページに書かれていないので書かない
 * - 室戸キンメ丼：始まった年は公式資料で確かめられないので書かない
 * - 室戸キンメ丼：全店共通価格であることは市のページにあるが、値段にかかわる情報なので書かない
 * - 皿鉢料理：「予約制の店が多い」は店ごとの営業情報なので説明文には書かない（note 用）
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { kochiSceneryTour } from './kochiScenes';
import { KOCHI_FOOD_HULLS } from './kochiFoodShapes.generated';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'aonori',
    level: 1,
    name: 'すじあおのり',
    shortName: 'あおのり',
    image: '/assets/foods/kochi/01_aonori.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '川の底から、香りだけ立ちのぼる。',
    source: 'https://www.chisanchisho.pref.kochi.lg.jp/life/dtl.php?hdnKey=1045',
    icon: { label: 'あおのり', shape: 'fish', baseColor: '#2a5a34', accentColor: '#7ab070' },
    reward: {
      unlockScore: 200,
      area: '四万十市・四万十町ほか四万十川流域',
      headline: '四万十川の川底で冬に育つ、細い糸のような青のり。',
      tip: '高知県の公式サイトは、天然のすじあおのりは四万十川産が全国シェアの八、九割を占めると書いている。流域では乾したものを軽くあぶってそのまま食べる。',
      links: searchLinks('四万十市', 'すじあおのり'),
    },
  },
  {
    id: 'dorome',
    level: 2,
    name: 'どろめ',
    shortName: 'どろめ',
    image: '/assets/foods/kochi/02_dorome.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: 'ゆでない、という食べかた。',
    source: 'https://www.chisanchisho.pref.kochi.lg.jp/life/dtl.php?hdnKey=1027',
    icon: { label: 'どろめ', shape: 'fish', baseColor: '#e0e4e8', accentColor: '#6a9a3a' },
    reward: {
      unlockScore: 500,
      area: '安芸市から須崎市の沿岸、田野浦',
      headline: '高知では、ゆでずに生のまま食べるしらすをどろめと呼ぶ。緑のぬたを添えるのが土佐流。',
      tip: '高知県の公式サイトによると、新鮮なしらすはにんにくぬたを添えたり三杯酢をかけたりして生のまま食べる。旬は春から秋。',
      links: searchLinks('高知県', 'どろめ'),
    },
  },
  {
    id: 'ice-krin',
    level: 3,
    name: 'アイスクリン',
    shortName: 'アイスクリン',
    image: '/assets/foods/kochi/03_ice_krin.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: 'アイスクリームより乳脂肪分が少なく、シャリッとしたシャーベットのような口当たり。ほのかにバナナの香りがして、後味はさっぱりしている。高知で長く親しまれてきたおやつ。',
    source:
      'https://kochi-tabi.jp/lp/gourmet/gourmet_dtl_13035.html',
    icon: { label: 'アイスクリン', shape: 'berry', baseColor: '#f3e7a1', accentColor: '#d9a35a' },
    reward: {
      unlockScore: 1000,
      area: '高知県内各地',
      headline: 'シャリッとさっぱりの冷たいおやつ',
      tip: '観光地や商店の店先、街路市、居酒屋のデザートなど、いろいろな所で食べられる。夏は国道沿いでも売られ、ビーチパラソルが目印になる。',
      links: searchLinks('高知市', 'アイスクリン'),
    },
  },
  {
    id: 'tosa-jiro-tamago',
    level: 4,
    name: '土佐ジローの卵',
    shortName: '土佐ジロー卵',
    image: '/assets/foods/kochi/04_tosa_jiro_tamago.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '小さいのに、黄身が立つ。',
    source: 'https://www.chisanchisho.pref.kochi.lg.jp/life/dtl.php?hdnKey=996',
    icon: { label: '土佐ジロー卵', shape: 'roe', baseColor: '#e8d8b8', accentColor: '#e07828' },
    reward: {
      unlockScore: 1800,
      area: '高知県内',
      headline: '土佐地鶏とロードアイランドレッドをかけ合わせた高知県特産の鶏。その卵は小ぶり。',
      tip: '高知県の公式サイトによると、卵はM玉のおよそ三分の二の大きさで、黄身が多く味が濃い。鶏そのものは高知県特産の地鶏として育てられている。',
      links: searchLinks('高知県', '土佐ジロー'),
    },
  },
  {
    id: 'shimanto-kawaebi-karaage',
    level: 5,
    name: '四万十の川えびの唐揚げ',
    shortName: '川えびの唐揚げ',
    image: '/assets/foods/kochi/05_shimanto_kawaebi_karaage.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '四万十川にすむ川エビを唐揚げにし、殻ごと香ばしく食べる。テナガエビなど3種類がいて、オスのはさみが体長を超えるほど長いのが特徴。体長は3〜10cmほど。',
    source:
      'https://www.shimanto-kankou.com/kanko/eat/tenagaebi.html',
    icon: { label: '川えび', shape: 'roe', baseColor: '#e2683a', accentColor: '#f2c94c' },
    reward: {
      unlockScore: 3000,
      area: '四万十市',
      headline: '長いはさみごと揚げる川エビ',
      tip: '漁期は5月から8月。伝統漁法のコロバシ漁や柴漬け漁でとる。唐揚げや塩焼きのほか、キュウリと炊いたり、そうめんに入れたりもする。',
      links: searchLinks('四万十市', '川エビの唐揚げ'),
    },
  },
  {
    id: 'nabeyaki-ramen',
    level: 6,
    name: '鍋焼きラーメン',
    shortName: '鍋焼きラーメン',
    image: '/assets/foods/kochi/06_nabeyaki_ramen.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '器まで、この味の一部です。',
    source: 'https://www.city.susaki.lg.jp/life/detail.php?hdnKey=177',
    icon: { label: '鍋焼きラーメン', shape: 'ramen', baseColor: '#8a5a38', accentColor: '#e8a828' },
    reward: {
      unlockScore: 4500,
      area: '須崎市',
      headline: '須崎市の公式が「一番のポイントは器が土鍋であること」と書く、最後まで熱いままのラーメン。',
      tip: 'スープは親鳥の鶏がらしょうゆ味、麺は腰のあるストレートの細麺、具はねぎ、ちくわ、生卵。付け合わせにたくあんが出る。',
      links: searchLinks('須崎市', '鍋焼きラーメン'),
    },
  },
  {
    id: 'shimizu-saba-sashimi',
    level: 7,
    name: '清水さばの刺身',
    shortName: '清水さばの刺身',
    image: '/assets/foods/kochi/07_shimizu_saba_sashimi.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '土佐清水で立て縄漁により一年を通してとる、瀬付きのゴマサバ。体にゴマを散らしたような斑点がある。漁協に水揚げされた脂の乗った高鮮度のものを「土佐の清水さば」と認定している。',
    source:
      'https://www.city.tosashimizu.kochi.jp/kanko/g02_shimizusaba02.html',
    icon: { label: '清水さば', shape: 'sashimi-platter', baseColor: '#e8a8a0', accentColor: '#4f6f8f' },
    reward: {
      unlockScore: 6500,
      area: '土佐清水市',
      headline: 'ゴマ模様の鯖を刺身で',
      tip: '釣り上げたサバを活きたまま港へ持ち帰り、活魚としても扱う。秋から冬にとれるサバはトロのような脂が乗り、刺身に向く。',
      links: searchLinks('土佐清水市', '清水さば 刺身'),
      official: [{ label: '土佐清水市「土佐の清水さば」', url: 'https://www.city.tosashimizu.kochi.jp/kanko/g02_shimizusaba02.html' }],
    },
  },
  {
    id: 'utsubo-tataki',
    level: 8,
    name: 'うつぼのたたき',
    shortName: 'うつぼたたき',
    image: '/assets/foods/kochi/08_utsubo_tataki.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '見た目で損をしてきた魚。',
    source: 'https://www.chisanchisho.pref.kochi.lg.jp/life/dtl.php?hdnKey=1020',
    icon: { label: 'うつぼたたき', shape: 'fish', baseColor: '#7a6038', accentColor: '#f0ece0' },
    reward: {
      unlockScore: 9000,
      area: '須崎市',
      headline: '秋から冬、岩礁でとれる細長い魚。皮はゼラチン質で、たたきにすると独特の歯ごたえに。',
      tip: '高知県の公式サイトによれば、うつぼは秋から冬にかけて沿岸の岩礁で漁獲され、主要水揚げ地は須崎。皮はゼラチン質で食感に優れ、たたきや唐揚げにする。',
      links: searchLinks('須崎市', 'うつぼのたたき'),
    },
  },
  {
    id: 'muroto-kinme-don',
    level: 9,
    name: '室戸キンメ丼',
    shortName: '室戸キンメ丼',
    image: '/assets/foods/kochi/09_muroto_kinme_don.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '室戸沖でとれた金目鯛を使うご当地丼。ご飯に室戸産金目鯛の照焼きと地魚の刺身をのせ、各店独自の金目鯛のダシを添える。具材は高知県産にこだわる。',
    source:
      'https://www.city.muroto.kochi.jp/pages/page0289.php',
    icon: { label: 'キンメ丼', shape: 'rice-bowl', baseColor: '#d94a33', accentColor: '#f3e9d2' },
    reward: {
      unlockScore: 12000,
      area: '室戸市',
      headline: '照焼きと刺身をのせた金目鯛の丼',
      tip: 'キンメダイは深海にすむ魚。室戸市の東側は海底が急に深くなる地形で、沿岸近くの漁場でとって鮮度の高いまま水揚げできる。',
      links: searchLinks('室戸市', '室戸キンメ丼'),
      official: [{ label: '室戸市「室戸キンメ丼」', url: 'https://www.city.muroto.kochi.jp/pages/page0289.php' }],
    },
  },
  {
    id: 'saba-sugata-zushi',
    level: 10,
    name: 'さばの姿ずし',
    shortName: 'さばの姿ずし',
    image: '/assets/foods/kochi/10_saba_sugata_zushi.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '魚の形のまま、すしです。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/sabanosugatazushi_kochi.html',
    icon: { label: 'さばの姿ずし', shape: 'fish', baseColor: '#4a5a6a', accentColor: '#f0ece0' },
    reward: {
      unlockScore: 16000,
      area: '高知県内全域',
      headline: '背開きにしたさば一尾にすし飯を詰め、頭も尾もつけたまま姿のまま盛る、晴れの日のすし。',
      tip: '冠婚葬祭や神事に欠かせない皿鉢料理の一品。塩で半日から一日しめ、酢に12時間ほど漬けてから、しょうがとごまを混ぜたすし飯を腹に詰める。',
      links: searchLinks('高知県', 'さばの姿寿司'),
    },
  },
  {
    id: 'katsuo-tataki',
    level: 11,
    name: 'かつおのたたき',
    shortName: 'かつおのたたき',
    image: '/assets/foods/kochi/11_katsuo_tataki.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '外は炎、中はまだ海の色。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/katuonotataki_kochi.html',
    icon: { label: 'かつおのたたき', shape: 'sashimi-platter', baseColor: '#a82a30', accentColor: '#3a2a24' },
    reward: {
      unlockScore: 22000,
      area: '高知県内全域（中土佐町久礼、土佐市宇佐などが漁の拠点）',
      headline: '表面だけを炎であぶり、中は生のまま。厚めに切った切り身に、にんにくと柚子酢のたれをかける。',
      tip: '焼き網にのせて炎の先にかざし、三面をあぶってから厚めの刺身に切る。昔ながらの方法では藁を使う。薄切りにんにくは欠かせない。',
      links: searchLinks('高知県', 'かつおのたたき'),
    },
  },
  {
    id: 'sawachi-ryori',
    level: 12,
    name: '皿鉢料理',
    shortName: '皿鉢料理',
    image: '/assets/foods/kochi/12_sawachi_ryori.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '特定の料理名ではなく盛り方の様式。36〜39cmの大皿に、刺身、かつおのたたき、姿ずし、田舎ずし、煮物、揚げ物などを、山の幸と海の幸を取り合わせて盛る。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/sawachiryori_kochi.html',
    icon: { label: '皿鉢', shape: 'feast', baseColor: '#3a2a24', accentColor: '#c0392b' },
    reward: {
      unlockScore: 30000,
      area: '高知県内全域',
      headline: '大皿に山海の幸を盛るおきゃく料理',
      tip: '客を招いて宴会を開くことを高知では「おきゃく」と呼ぶ。大皿を大勢で囲んで小皿に取り分け、料理が減ると補充する。藩政時代の武家の宴が起こりとされる。',
      links: searchLinks('高知市', '皿鉢料理'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const kochiFoodStage: StageDefinition = {
  regionId: 'kochi',
  regionName: '高知県',
  mode: 'food',
  title: '高知めしマージ',
  theme: {
    primaryColor: '#1a5a7a',
    secondaryColor: '#e0a840',
    accentColor: '#f0c868',
    backgroundColor: '#101820',
  },
  /** 背景ツアー「高知めぐり」。軸は一日の時間。夜明けの室戸岬から、月の出た桂浜へ。 */
  scenery: {
    tour: kochiSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, KOCHI_FOOD_HULLS),
};
